import React, { useContext, RefObject, useEffect, useState } from 'react';
import {
    Dropzone,
    DownloadOutlined,
    Text,
    Progress,
    styled,
    spacing,
    Flex,
} from '@landr/maestro';
import { Trans } from '@lingui/macro';
import { FormFieldComponentProps } from 'components/Form';
import { FormFields } from 'types/fields';
import { NewPackFormFields, NewPackFormKeys } from '../NewPack.types';
import { UploadFieldContext } from './UploadFieldContext';

const Centered = styled.div`
    text-align: center;
    margin: ${spacing('sm')} 0;
`;

const readableFilesize = (kb: number): string => {
    const mb = 1000;
    const gb = 1000 * mb;

    if (kb > gb) {
        return `${(kb / gb).toFixed(3)}gb`;
    }

    if (kb > mb) {
        return `${(kb / mb).toFixed(2)}mb`;
    }

    return `${kb}kb`;
};

const UploadInput: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = ({ children, value, name, form, isInvalid }) => {
    const [uploaded, setUploaded] = useState<number | false>(false);
    const [total, setTotal] = useState(1);
    const [progression, setProgression] = useState<number | 'indeterminate'>(
        'indeterminate',
    );
    const [progressionString, setProgressionString] = useState('');

    const {
        getUploadStatus,
        setUploadStatus,
        doUpload,
        getUploadProgress,
        fieldProgress,
    } = useContext(UploadFieldContext);
    const uploadStatus = getUploadStatus(name);

    const handleOnClick = (input: RefObject<HTMLInputElement>): void => {
        input?.current?.click();
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        e.preventDefault();

        let files = e.target?.files;
        files = files ? files : new FileList();

        const file = files[0];

        setUploadStatus(name, 'pending');

        form.setFieldTouched(name);
        form.setFieldValue(`${name}Id`, '');

        if (file) {
            form.setFieldValue(name, file);
            doUpload(name, file);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        const file = e.dataTransfer?.files[0];

        if (file) {
            form.setFieldValue(name, file);
            doUpload(name, file);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
        e.stopPropagation();
        e.preventDefault();
    };

    const isLoading = uploadStatus === 'loading';

    useEffect(() => {
        const progress = getUploadProgress(name);

        setUploaded(progress.uploaded);
        setTotal(progress.total);
    }, [fieldProgress, name, getUploadProgress]);

    useEffect(() => {
        if (uploaded) {
            setProgression(uploaded / total);

            const uploadedString = readableFilesize(uploaded);
            const totalString = readableFilesize(total);

            setProgressionString(`${uploadedString} of ${totalString}`);
        }
    }, [uploaded, total]);

    return (
        <Dropzone
            wrapperProps={{
                onClick: handleOnClick,
                onDrop: handleDrop,
                onDragOver: handleDragOver,
                onChange: handleOnChange,
            }}
            isInvalid={isInvalid}
            isLoading={isLoading}
        >
            <Dropzone.Content>
                {isLoading && (
                    <>
                        <Centered>
                            <Progress
                                progress={progression}
                                size={60}
                                strokeWidth={1}
                            />
                        </Centered>
                        <Centered>
                            <Text>Uploading {value.name}...</Text>
                        </Centered>
                        <Centered>
                            <Text size="sm">
                                {progressionString && `${progressionString}`}
                            </Text>
                        </Centered>
                    </>
                )}
                {!isLoading && value && <Text>{value.name}</Text>}
                {!isLoading && !value && (
                    <>
                        <Dropzone.Icon icon={DownloadOutlined} size="lg" />
                        <Flex flexDirection="column" textAlign="center">
                            <Text>
                                <Trans>Drop your file or click to upload</Trans>
                            </Text>
                            {name === NewPackFormKeys.DemoUpload && (
                                <Text contrast="low">wav, mp3</Text>
                            )}
                            {name === NewPackFormKeys.ZipUpload && (
                                <Text contrast="low">zip</Text>
                            )}
                            {name === NewPackFormKeys.ArtworkUpload && (
                                <Text contrast="low">jpg, png - 4 MB</Text>
                            )}
                            {name === NewPackFormKeys.CsvUpload && (
                                <Text contrast="low">csv</Text>
                            )}
                        </Flex>
                    </>
                )}
            </Dropzone.Content>
        </Dropzone>
    );
};

export const CsvUpload: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (props) => {
    return (
        <div data-testid={NewPackFormKeys.CsvUpload}>
            <UploadInput {...props}>
                <Dropzone.Icon icon={DownloadOutlined} size="lg" />
                <Text>
                    Drop your file or click to{' '}
                    <Text contrast="high">browse</Text>
                </Text>
            </UploadInput>
        </div>
    );
};

export const DemoUpload: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (props) => {
    return (
        <div data-testid={NewPackFormKeys.DemoUpload}>
            <UploadInput {...props}>
                <Dropzone.Icon icon={DownloadOutlined} size="lg" />
                <Text>
                    <Trans>Drop your file or click to upload</Trans>
                    <Text contrast="low">wav, mp3 - X MB</Text>
                </Text>
            </UploadInput>
        </div>
    );
};

export const ImageUpload: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (props) => {
    return (
        <div data-testid={NewPackFormKeys.ArtworkUpload}>
            <UploadInput {...props}>
                <Dropzone.Icon icon={DownloadOutlined} size="lg" />
                <Text>
                    Drop your file or click to{' '}
                    <Text contrast="high">browse</Text>
                </Text>
            </UploadInput>
        </div>
    );
};

export const PackUpload: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (props) => {
    return (
        <div data-testid={NewPackFormKeys.ZipUpload}>
            <UploadInput {...props}></UploadInput>
        </div>
    );
};
